<template>
  <div class="usage-history">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('usage.usageRecords') }}</h2>
      </div>
    </div>

    <!-- 首屏 batch 加载失败：inline Alert（toast 仍由 presenter 汇总一条） -->
    <a-alert
      v-if="pageErrorMessage"
      type="error"
      show-icon
      closable
      class="page-error-alert"
      :message="pageErrorMessage"
      @close="clearPageError"
    />

    <!-- 管理员：按用户查询 -->
    <a-alert
      v-if="canQueryOtherUsers"
      type="info"
      show-icon
      class="admin-usage-filter"
      :message="$t('usage.adminTargetUserHint')"
    >
      <template #description>
        <a-space wrap align="start">
          <a-form layout="inline" class="admin-usage-form">
            <a-form-item :label="$t('profile.userId')">
              <a-input-number
                v-model:value="adminTargetUserId"
                :min="1"
                :precision="0"
                :placeholder="$t('profile.userId')"
                style="width: 160px"
              />
            </a-form-item>
            <a-form-item>
              <a-button type="primary" @click="applyAdminTargetUser">
                {{ $t('common.search') }}
              </a-button>
            </a-form-item>
            <a-form-item>
              <a-button @click="clearAdminTargetUser">{{ $t('usage.viewOwnUsage') }}</a-button>
            </a-form-item>
          </a-form>
        </a-space>
      </template>
    </a-alert>

    <!-- 配额概览卡片 -->
    <a-row :gutter="16" class="quota-overview">
      <a-col :span="12">
        <a-card :title="$t('usage.characterUsage')" :bordered="false">
          <div class="quota-card">
            <div class="quota-info">
              <div class="quota-item">
                <span class="label">{{ $t('usage.totalQuota') }}:</span>
                <span class="value">{{ characterQuota?.total_quota || 0 }}</span>
              </div>
              <div class="quota-item">
                <span class="label">{{ $t('usage.used') }}:</span>
                <span class="value used">{{ characterQuota?.used_quota || 0 }}</span>
              </div>
              <div class="quota-item">
                <span class="label">{{ $t('usage.remaining') }}:</span>
                <span class="value remaining">{{ characterQuota?.remaining_quota || 0 }}</span>
              </div>
            </div>
            <a-progress 
              :percent="characterUsagePercent" 
              :status="(characterQuota?.remaining_quota ?? 0) <= 0 ? 'exception' : 'normal'"
              :stroke-color="(characterQuota?.remaining_quota ?? 0) <= 0 ? '#ff4d4f' : '#52c41a'"
            />
          </div>
        </a-card>
      </a-col>
      <a-col :span="12">
        <a-card :title="$t('usage.timeUsageQuota')" :bordered="false">
          <div class="quota-card">
            <div class="quota-info">
              <div class="quota-item">
                <span class="label">{{ $t('usage.totalQuota') }}:</span>
                <span class="value">{{ formatTimeAmount(timeQuota?.total_quota || 0) }}</span>
              </div>
              <div class="quota-item">
                <span class="label">{{ $t('usage.used') }}:</span>
                <span class="value used">{{ formatTimeAmount(timeQuota?.used_quota || 0) }}</span>
              </div>
              <div class="quota-item">
                <span class="label">{{ $t('usage.remaining') }}:</span>
                <span class="value remaining">{{ formatTimeAmount(timeQuota?.remaining_quota || 0) }}</span>
              </div>
            </div>
            <a-progress 
              :percent="timeUsagePercent" 
              :status="(timeQuota?.remaining_quota ?? 0) <= 0 ? 'exception' : 'normal'"
              :stroke-color="(timeQuota?.remaining_quota ?? 0) <= 0 ? '#ff4d4f' : '#52c41a'"
            />
          </div>
        </a-card>
      </a-col>
    </a-row>

    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="filters">
        <a-form-item :label="$t('usage.usageType')">
          <a-select
            v-model:value="filters.usage_type"
            :placeholder="$t('usage.usageType')"
            style="width: 120px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option value="character">{{ $t('usage.characterUsage') }}</a-select-option>
            <a-select-option value="time">{{ $t('usage.timeUsage') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('usage.serviceType')">
          <a-select
            v-model:value="filters.service_type"
            :placeholder="$t('usage.serviceType')"
            style="width: 150px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option value="video_translation">{{ $t('usage.videoTranslation') }}</a-select-option>
            <a-select-option value="document_translation">{{ $t('usage.documentTranslation') }}</a-select-option>
            <a-select-option value="message_translation">{{ $t('usage.messageTranslation') }}</a-select-option>
            <a-select-option value="hot_word_extraction">{{ $t('usage.hotWordExtraction') }}</a-select-option>
            <a-select-option value="hot_word_translation">{{ $t('usage.hotWordTranslation') }}</a-select-option>
            <a-select-option value="speech_to_text">{{ $t('usage.speechToText') }}</a-select-option>
            <a-select-option value="speech_translation">{{ $t('usage.speechTranslation') }}</a-select-option>
            <a-select-option value="ai_chat">{{ $t('usage.aiChat') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('common.dateRange')">
          <a-range-picker
            v-model:value="dateRange"
            @change="handleDateRangeChange"
            :placeholder="[$t('usage.startDate'), $t('usage.endDate')]"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">
              <template #icon><SearchOutlined /></template>
              {{ $t('common.search') }}
            </a-button>
            <a-button @click="handleReset">
              {{ $t('user.reset') }}
            </a-button>
            <a-button @click="refreshData" :loading="loading">
              <template #icon><ReloadOutlined /></template>
              {{ $t('common.refresh') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 消费记录列表 -->
    <a-card :title="$t('usage.consumptionRecords')" :bordered="false">
      <a-table
        :columns="columns"
        :data-source="usageRecords"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
        class="usage-table"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'usage_type'">
            <a-tag :color="record.usage_type === 'character' ? 'blue' : 'green'">
              {{ record.usage_type === 'character' ? $t('usage.characterUsage') : $t('usage.timeUsage') }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'amount'">
            <span v-if="record.usage_type === 'character'">
              {{ record.amount }} {{ $t('usage.characters') }}
            </span>
            <span v-else>
              {{ formatTimeAmount(record.amount) }}
            </span>
          </template>
          <template v-else-if="column.key === 'created_at'">
            {{ formatDate(record.created_at) }}
          </template>
          <template v-else-if="column.key === 'service_type'">
            <a-tag color="purple">{{ getServiceTypeText(record.service_type) }}</a-tag>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons-vue'
import { usageApi, type UsageQuota, type UsageRecord } from '@/modules/usage/api'
import { formatApiErrorMessage } from '@/shared/api/l1-error-presenter'
import { runAsyncBatchWithError } from '@/shared/composables/useAsyncResource'
import type { L1Error } from '@/shared/api/l1-error'
import type { BatchContext } from '@/shared/api/use-api-batch'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import { permissionManager, UserType } from '@/utils/permission'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

/** 与后端一致：拥有 admin 角色（effective）的管理员可代查他人用量 */
const canQueryOtherUsers = computed(() => permissionManager.hasRole(UserType.ADMIN))

/** 管理员查询目标用户 ID（与路由 query.user_id 同步） */
const adminTargetUserId = ref<number | undefined>(undefined)

const parseQueryUserId = (): number | undefined => {
  const raw = route.query.user_id
  if (raw === undefined || raw === null || raw === '') return undefined
  const n = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : undefined
}

watch(
  () => route.query.user_id,
  () => {
    if (!canQueryOtherUsers.value) return
    adminTargetUserId.value = parseQueryUserId()
  }
)

// 响应式数据
const loading = ref(false)
const pageError = ref<L1Error | null>(null)
const pageErrorMessage = computed(() =>
  pageError.value ? formatApiErrorMessage(pageError.value) : '',
)
const usageRecords = ref<UsageRecord[]>([])
const quotas = ref<UsageQuota[]>([])
const dateRange = ref<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

// 筛选条件
const filters = reactive({
  usage_type: undefined as 'character' | 'time' | undefined,
  service_type: undefined as string | undefined,
  start_date: undefined as string | undefined,
  end_date: undefined as string | undefined,
  limit: 20,
  offset: 0
})

// 分页状态
const paginationState = reactive({
  current: 1,
  pageSize: 20,
  total: 0
})

// 处理分页变化（通过 pagination 配置的回调）
const handlePaginationChange = async (page: number, pageSize: number) => {
  paginationState.current = page
  paginationState.pageSize = pageSize
  await loadUsageRecords()
}

// 使用 computed 返回分页配置，确保响应式更新
// Ant Design Vue 推荐在 pagination 配置对象中使用 onChange 和 onShowSizeChange 回调
const pagination = computed(() => ({
  current: paginationState.current,
  pageSize: paginationState.pageSize,
  total: paginationState.total,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('usage.totalRecords', { total }),
  // 使用 onChange 处理页码变化
  onChange: handlePaginationChange,
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: handlePaginationChange
}))

// 表格列定义
const columns = computed(() => [
  {
    title: t('usage.serviceType'),
    dataIndex: 'service_type',
    key: 'service_type',
    width: 120
  },
  {
    title: t('usage.usageType'),
    dataIndex: 'usage_type',
    key: 'usage_type',
    width: 100
  },
  {
    title: t('usage.consumptionAmount'),
    dataIndex: 'amount',
    key: 'amount',
    width: 120
  },
  {
    title: t('usage.apiName'),
    dataIndex: 'api_name',
    key: 'api_name',
    width: 150
  },
  {
    title: t('usage.projectType'),
    dataIndex: 'project_type',
    key: 'project_type',
    width: 120
  },
  {
    title: t('usage.consumptionTime'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 180
  }
])

// 计算属性
const characterQuota = computed(() => 
  quotas.value.find(q => q.usage_type === 'character')
)

const timeQuota = computed(() => 
  quotas.value.find(q => q.usage_type === 'time')
)

const characterUsagePercent = computed(() => {
  if (!characterQuota.value || characterQuota.value.total_quota === 0) return 0
  return Math.round((characterQuota.value.used_quota / characterQuota.value.total_quota) * 100)
})

const timeUsagePercent = computed(() => {
  if (!timeQuota.value || timeQuota.value.total_quota === 0) return 0
  return Math.round((timeQuota.value.used_quota / timeQuota.value.total_quota) * 100)
})

// 方法
const loadQuotas = async (batchCtx?: BatchContext) => {
  try {
    const uid = canQueryOtherUsers.value && adminTargetUserId.value
    const data = await usageApi.getMyQuota(
      uid ? { user_id: adminTargetUserId.value } : undefined,
      batchCtx?.requestConfig,
    )
    quotas.value = data
  } catch (error) {
    if (!batchCtx) {
      console.error('加载配额信息失败:', error)
    }
    throw error
  }
}

const loadUsageRecords = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  try {
    if (manageLoading) {
      loading.value = true
    }
    const params: Record<string, unknown> = {
      ...filters,
      limit: paginationState.pageSize,
      offset: (paginationState.current - 1) * paginationState.pageSize
    }

    if (canQueryOtherUsers.value && adminTargetUserId.value) {
      params.user_id = adminTargetUserId.value
    }

    // 如果没有设置日期范围，默认查询最近30天
    if (!filters.start_date && !filters.end_date) {
      params.days = 30
    }

    const response = await usageApi.getMyUsageRecords(
      params as {
        user_id?: number
        service_type?: string
        usage_type?: 'character' | 'time'
        start_date?: string
        end_date?: string
        days?: number
        limit?: number
        offset?: number
      },
      batchCtx?.requestConfig,
    )
    usageRecords.value = response.items
    paginationState.total = response.total
    if (response.limit) {
      paginationState.pageSize = response.limit
    }
  } catch (error) {
    if (!batchCtx) {
      console.error('加载消费记录失败:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loading.value = false
    }
  }
}

const refreshData = async () => {
  const { primaryError } = await runAsyncBatchWithError([
    (ctx) => loadQuotas(ctx),
    (ctx) => loadUsageRecords(ctx),
  ])
  pageError.value = primaryError
}

const clearPageError = () => {
  pageError.value = null
}

const handleFilterChange = () => {
  paginationState.current = 1
  loadUsageRecords()
}

const handleDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
  if (dates) {
    filters.start_date = dates[0].format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    filters.end_date = dates[1].format('YYYY-MM-DDTHH:mm:ss.SSSZ')
  } else {
    filters.start_date = undefined
    filters.end_date = undefined
  }
  handleFilterChange()
}

const handleSearch = () => {
  paginationState.current = 1
  loadUsageRecords()
}

const handleReset = () => {
  filters.usage_type = undefined
  filters.service_type = undefined
  filters.start_date = undefined
  filters.end_date = undefined
  
  // 重置为默认30天范围
  const endDate = dayjs()
  const startDate = endDate.subtract(30, 'day')
  dateRange.value = [startDate, endDate]
  
  paginationState.current = 1
  loadUsageRecords()
}

// 处理表格变化（排序和筛选）
// 注意：分页变化已经通过 pagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleTableChange = (pag: any, filters: any, sorter: any) => {
  // 分页变化已经在 pagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
}

/** 管理员：按输入的用户 ID 查询并写入路由，便于分享链接 */
const applyAdminTargetUser = async () => {
  if (adminTargetUserId.value == null || adminTargetUserId.value < 1) {
    message.warning(t('usage.invalidUserId'))
    return
  }
  await router.replace({ query: { ...route.query, user_id: String(adminTargetUserId.value) } })
  paginationState.current = 1
  await refreshData()
}

/** 管理员：恢复查看当前登录用户 */
const clearAdminTargetUser = async () => {
  adminTargetUserId.value = undefined
  const q = { ...route.query } as Record<string, string | string[] | undefined>
  delete q.user_id
  await router.replace({ query: q })
  paginationState.current = 1
  await refreshData()
}

const formatDate = (dateStr: string) => {
  return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss')
}

const formatTimeAmount = (seconds: number) => {
  // 直接显示秒数，保留2位小数
  return `${Math.round(seconds * 100) / 100} ${t('usage.seconds')}`
}

const getServiceTypeText = (serviceType: string) => {
  const typeMap: Record<string, string> = {
    'video_translation': t('usage.videoTranslation'),
    'document_translation': t('usage.documentTranslation'),
    'message_translation': t('usage.messageTranslation'),
    'hot_word_extraction': t('usage.hotWordExtraction'),
    'hot_word_translation': t('usage.hotWordTranslation'),
    'speech_to_text': t('usage.speechToText'),
    'speech_translation': t('usage.speechTranslation'),
    'ai_chat': t('usage.aiChat')
  }
  return typeMap[serviceType] || serviceType
}

// 生命周期
onMounted(async () => {
  // 设置默认日期范围为最近30天
  const endDate = dayjs()
  const startDate = endDate.subtract(30, 'day')
  dateRange.value = [startDate, endDate]

  if (!canQueryOtherUsers.value && route.query.user_id) {
    const q = { ...route.query } as Record<string, string | string[] | undefined>
    delete q.user_id
    await router.replace({ query: q })
  } else if (canQueryOtherUsers.value) {
    adminTargetUserId.value = parseQueryUserId()
  }

  await refreshData()
})
</script>

<style lang="less" scoped>
.usage-history {
  padding: 24px;
}

.admin-usage-filter {
  margin-bottom: 16px;
}

.page-error-alert {
  margin-bottom: 16px;
}

.page-header {
  margin-bottom: 24px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
  }
}

.quota-overview {
  margin-bottom: 24px;
  
  .quota-card {
    .quota-info {
      margin-bottom: 16px;
      
      .quota-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        
        .label {
          color: #8c8c8c;
          font-size: 14px;
        }
        
        .value {
          font-weight: 600;
          font-size: 14px;
          
          &.used {
            color: #1890ff;
          }
          
          &.remaining {
            color: #52c41a;
          }
        }
      }
    }
  }
}

.search-card {
  margin-bottom: 24px;
}

:deep(.ant-card-head-title) {
  font-weight: 600;
}

:deep(.ant-progress-text) {
  font-size: 12px;
}
</style>

